// Which holiday theme plays on a given calendar day.
//
// A theme has a window of one or more days; outside every window the answer is None. Where windows
// overlap the narrowest wins the day, and equal widths go to the earlier theme in registry order. So a
// single-day holiday (Valentine's Day) shows inside a long window (Chinese New Year, Pride) and the
// long window resumes the next day.
//
// Dates come from the calendars themselves rather than a per-year list wherever the platform has the
// calendar: Chinese (New Year, Lantern Festival), Islamic Umm al-Qura (Eid, the Saudi civil date — a
// local moon sighting can differ by a day) and Persian (Nowruz). Diwali has no calendar in `Intl`, so it
// is a table, and a year outside the table is logged and plays no Diwali rather than a guessed date.

import { HOLIDAY_THEME_ORDER, NONE_THEME_ID } from './registry';
import type { HolidayThemeId } from './registry';

/** A calendar day as the viewer's device reads it. */
export interface CalendarDay {
  year: number;
  /** 1-12 */
  month: number;
  day: number;
}

export type HolidayRegion = 'US' | 'CA';

type ScheduledThemeId = Exclude<HolidayThemeId, typeof NONE_THEME_ID>;

interface DayWindow {
  themeId: ScheduledThemeId;
  /** Inclusive day numbers (days since the epoch, UTC). */
  start: number;
  end: number;
}

const MS_PER_DAY = 86_400_000;
const SUNDAY = 0;
const MONDAY = 1;
const THURSDAY = 4;

const dayNumber = (year: number, month: number, day: number): number => Math.round(Date.UTC(year, month - 1, day) / MS_PER_DAY);

/** The n-th (1-based) given weekday of a month, as a day number. */
function nthWeekday(year: number, month: number, weekday: number, n: number): number {
  const firstWeekday = new Date(Date.UTC(year, month - 1, 1)).getUTCDay();
  return dayNumber(year, month, 1 + ((weekday - firstWeekday + 7) % 7) + 7 * (n - 1));
}

// Diwali main day (Lakshmi Puja), verified 2026-10-02: 2026-2029 against diwali.info and
// awarenessdays.com; 2030-2035 against diwali.info.
const DIWALI: Record<number, [month: number, day: number]> = {
  2026: [11, 8],
  2027: [10, 29],
  2028: [10, 17],
  2029: [11, 5],
  2030: [10, 26],
  2031: [11, 14],
  2032: [11, 2],
  2033: [10, 22],
  2034: [11, 10],
  2035: [10, 30],
};
export const DIWALI_FIRST_YEAR = 2026;
export const DIWALI_LAST_YEAR = 2035;

const formatters = new Map<string, Intl.DateTimeFormat>();

/** Day numbers within a Gregorian year on which a non-Gregorian calendar reads month/day. May hold two. */
function calendarDates(calendar: string, month: number, day: number, year: number): number[] {
  let fmt = formatters.get(calendar);
  if (!fmt) {
    fmt = new Intl.DateTimeFormat(`en-u-ca-${calendar}`, { month: 'numeric', day: 'numeric', timeZone: 'UTC' });
    formatters.set(calendar, fmt);
  }
  const hits: number[] = [];
  for (let n = dayNumber(year, 1, 1); n < dayNumber(year + 1, 1, 1); n++) {
    const parts = fmt.formatToParts(new Date(n * MS_PER_DAY));
    // A leap month reads like "5bis", which is NaN here and never equals a plain month number.
    const m = Number(parts.find((p) => p.type === 'month')?.value);
    const d = Number(parts.find((p) => p.type === 'day')?.value);
    if (m === month && d === day) hits.push(n);
  }
  return hits;
}

const loggedDiwaliYears = new Set<number>();

function windowsForYear(year: number, region: HolidayRegion): DayWindow[] {
  const out: DayWindow[] = [];
  const add = (themeId: ScheduledThemeId, start: number, end: number = start) => out.push({ themeId, start, end });

  add('winter', dayNumber(year, 12, 1), dayNumber(year + 1, 1, 2));
  for (const n of calendarDates('chinese', 1, 1, year)) add('chineseNewYear', n, n + 14);
  for (const n of calendarDates('islamic-umalqura', 10, 1, year)) add('eid', n, n + 2);
  for (const n of calendarDates('islamic-umalqura', 12, 10, year)) add('eid', n, n + 3);
  const diwali = DIWALI[year];
  if (diwali) add('diwali', dayNumber(year, diwali[0], diwali[1]));
  add('halloween', dayNumber(year, 10, 31));
  add('thanksgiving', region === 'US' ? nthWeekday(year, 11, THURSDAY, 4) : nthWeekday(year, 10, MONDAY, 2));
  for (const n of calendarDates('persian', 1, 1, year)) add('nowruz', n);
  add('earthDay', dayNumber(year, 4, 22));
  add('pride', dayNumber(year, 6, 1), dayNumber(year, 6, 30));
  add('womensDay', dayNumber(year, 3, 8));
  add('valentines', dayNumber(year, 2, 14));
  add('mothersDay', nthWeekday(year, 5, SUNDAY, 2));
  add('fathersDay', nthWeekday(year, 6, SUNDAY, 3));
  return out;
}

/** The theme that plays on `day`, or None when no window holds it. */
export function scheduledThemeId(day: CalendarDay, region: HolidayRegion): HolidayThemeId {
  if (!DIWALI[day.year] && !loggedDiwaliYears.has(day.year)) {
    loggedDiwaliYears.add(day.year);
    console.error('[holiday] no Diwali date for this year; Diwali plays no theme', {
      event: 'holiday_diwali_year_not_in_table',
      year: day.year,
      firstYear: DIWALI_FIRST_YEAR,
      lastYear: DIWALI_LAST_YEAR,
    });
  }
  const today = dayNumber(day.year, day.month, day.day);
  // The previous year's windows are included because Winter holidays run into January.
  const candidates = [...windowsForYear(day.year - 1, region), ...windowsForYear(day.year, region)].filter(
    (w) => w.start <= today && today <= w.end,
  );
  if (candidates.length === 0) return NONE_THEME_ID;
  candidates.sort(
    (a, b) => a.end - a.start - (b.end - b.start) || HOLIDAY_THEME_ORDER.indexOf(a.themeId) - HOLIDAY_THEME_ORDER.indexOf(b.themeId),
  );
  return candidates[0].themeId;
}

// Country-specific IANA zones (zone.tab), plus legacy aliases browsers may still report.
const CA_TIME_ZONES: readonly string[] = [
  'America/St_Johns',
  'America/Halifax',
  'America/Glace_Bay',
  'America/Moncton',
  'America/Goose_Bay',
  'America/Blanc-Sablon',
  'America/Toronto',
  'America/Iqaluit',
  'America/Atikokan',
  'America/Winnipeg',
  'America/Resolute',
  'America/Rankin_Inlet',
  'America/Regina',
  'America/Swift_Current',
  'America/Edmonton',
  'America/Cambridge_Bay',
  'America/Inuvik',
  'America/Vancouver',
  'America/Creston',
  'America/Dawson_Creek',
  'America/Fort_Nelson',
  'America/Whitehorse',
  'America/Dawson',
  'America/Montreal',
  'America/Nipigon',
  'America/Thunder_Bay',
  'America/Rainy_River',
  'America/Pangnirtung',
  'America/Yellowknife',
  'Canada/Eastern',
  'Canada/Central',
  'Canada/Mountain',
  'Canada/Pacific',
  'Canada/Atlantic',
  'Canada/Newfoundland',
  'Canada/Saskatchewan',
  'Canada/Yukon',
];

const US_TIME_ZONES: readonly string[] = [
  'America/New_York',
  'America/Detroit',
  'America/Kentucky/Louisville',
  'America/Kentucky/Monticello',
  'America/Indiana/Indianapolis',
  'America/Indiana/Vincennes',
  'America/Indiana/Winamac',
  'America/Indiana/Marengo',
  'America/Indiana/Petersburg',
  'America/Indiana/Vevay',
  'America/Chicago',
  'America/Indiana/Tell_City',
  'America/Indiana/Knox',
  'America/Menominee',
  'America/North_Dakota/Center',
  'America/North_Dakota/New_Salem',
  'America/North_Dakota/Beulah',
  'America/Denver',
  'America/Boise',
  'America/Phoenix',
  'America/Los_Angeles',
  'America/Anchorage',
  'America/Juneau',
  'America/Sitka',
  'America/Metlakatla',
  'America/Yakutat',
  'America/Nome',
  'America/Adak',
  'Pacific/Honolulu',
  'America/Indianapolis',
  'America/Louisville',
  'America/Knox_IN',
  'US/Eastern',
  'US/Central',
  'US/Mountain',
  'US/Pacific',
  'US/Arizona',
  'US/Alaska',
  'US/Aleutian',
  'US/Hawaii',
  'US/Michigan',
];

/**
 * A recognized Canadian or US device time zone selects its Thanksgiving calendar. Browser language
 * is a fallback: US English is common in Canada and does not identify where the viewer is.
 * Outside those zones, an explicit locale region wins; otherwise the existing Canadian default applies.
 */
export function holidayRegion(locale: string, timeZone: string): HolidayRegion {
  if (CA_TIME_ZONES.includes(timeZone)) return 'CA';
  if (US_TIME_ZONES.includes(timeZone)) return 'US';
  const subtag = locale.split('-').find((part, i) => i > 0 && /^[A-Za-z]{2}$/.test(part))?.toUpperCase();
  if (subtag === 'US') return 'US';
  if (subtag === 'CA') return 'CA';
  return 'CA';
}

/** The viewer's device calendar day. */
export function localCalendarDay(date: Date): CalendarDay {
  return { year: date.getFullYear(), month: date.getMonth() + 1, day: date.getDate() };
}
